import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_TIME,
  DATE_TYPE_DATETIME,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  propTypes,
} from '../../util/types';
import { subtractTime, isDST } from '../../util/dates';

import css from './OrderBreakdown.module.css';

const DSTMaybe = props => {
  const { startDate, endDate, isStart, dateType, timeZone } = props;
  const isDayTimeRange = dateType === DATE_TYPE_DATETIME;
  if (!isDayTimeRange) {
    return null;
  }

  const isStartInDST = isDST(startDate, timeZone);
  const isEndInDST = isDST(endDate, timeZone);
  const isDSTChanged = isStartInDST !== isEndInDST;
  const showDSTMsgForStart = isDSTChanged && isStart && isStartInDST;
  const showDSTMsgForEnd = isDSTChanged && !isStart && isEndInDST;

  return showDSTMsgForStart || showDSTMsgForEnd ? (
    <div className={css.itemLabel}>
      <FormattedMessage id="OrderBreakdown.bookingWithDSTInEffect" />
    </div>
  ) : null;
};

const BookingPeriod = props => {
  const intl = useIntl();
  const { startDate, endDate, dateType, timeZone } = props;

  const locale = intl.locale || [];
  const options = { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timeZone || undefined };

  const startTimeStr = new Date(startDate).toLocaleTimeString(locale, options);
  const endTimeStr = new Date(endDate).toLocaleTimeString(locale, options);

  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingStartTime" />
          </div>
          <div className={css.dayInfo}>{startTimeStr}</div>
        </div>

        <div className={css.bookingPeriodSectionRight}>
          <div className={css.dayLabel}>
            <FormattedMessage id="OrderBreakdown.bookingEndTime" />
          </div>
          <div className={css.dayInfo}>{endTimeStr}</div>
        </div>
      </div>
    </>
  );
};

/**
 * A line-item to show booking period for the OrderBreakdown
 *
 * @component
 * @param {Object} props
 * @param {propTypes.booking?} props.booking
 * @param {LINE_ITEM_NIGHT | LINE_ITEM_DAY | LINE_ITEM_HOUR} props.code
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @param {string} props.timeZone IANA time zone name
 * @returns {JSX.Element} line-item element for the order breakdown
 */
const LineItemBookingTimePeriod = props => {
  const { booking, code, dateType, timeZone, startTime, endTime } = props;

  if (!booking) {
    return null;
  }
  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const showInclusiveEndDate = [LINE_ITEM_DAY].includes(code);
  const endDay = showInclusiveEndDate ? subtractTime(localEndDateRaw, 1, 'days') : localEndDateRaw;

  return (
    <>
      <div className={css.lineItem}>
        <BookingPeriod
          startDate={localStartDate}
          endDate={endDay}
          dateType={dateType}
          timeZone={timeZone}
          startTime={startTime}
          endTime={endTime}
        />
      </div>
      <hr className={css.totalDivider} />
    </>
  );
};

export default LineItemBookingTimePeriod;
